import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SpanStatisticsBaseline } from "../../api/client";
import { formatDuration } from "../../utils/formatting";

export interface BaselineTooltipProps {
  baseline: SpanStatisticsBaseline | null | undefined;
  children: React.ReactElement;
  placement?: "top-start" | "top" | "top-end" | "bottom-start" | "bottom" | "bottom-end";
  showArrow?: boolean;
}

export function BaselineTooltip({
  baseline,
  children,
  placement = "top-start",
  showArrow: arrow = false,
}: BaselineTooltipProps) {
  const theme = useTheme();
  if (!baseline) return children;

  const slotPropsTooltip = {
    tooltip: {
      sx: {
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
        boxShadow: theme.shadows[6],
      },
      arrow: { 
        sx: { 
          color: alpha(theme.palette.text.primary, 0.16) 
        } 
      },
    },
  };

  const sxContentBox = {
    minWidth: 180,
    px: 1.25,
    py: 0.9,
  };

  const title = (
    <Box sx={sxContentBox}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            p50
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            {formatDuration(baseline.p50_duration)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            p90
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            {formatDuration(baseline.p90_duration)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            p95
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            {formatDuration(baseline.p95_duration)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Tooltip arrow={arrow} placement={placement} title={title} slotProps={slotPropsTooltip}>
      {children}
    </Tooltip>
  );
}
